import { useCallback, useEffect, useRef, useState } from "react";

/** Lifecycle of a single "where is the user" request. */
export enum EGeolocationStatus {
  IDLE = "idle",
  LOCATING = "locating",
  SUCCESS = "success",
  DENIED = "denied",
  UNAVAILABLE = "unavailable",
  TIMEOUT = "timeout",
  /** No `navigator.geolocation`, or the page is not a secure context. */
  UNSUPPORTED = "unsupported"
}

export type GeolocationFix = {
  longitude: number;
  latitude: number;
  /** Radius of the confidence circle the browser reports, in meters. */
  accuracy: number;
};

export type GeolocationResult = {
  status: EGeolocationStatus;
  fix: GeolocationFix | null;
};

/**
 * Spec-defined `GeolocationPositionError` codes. Compared as plain numbers
 * rather than through the instance constants, since the error object handed to
 * the callback is not guaranteed to carry them.
 */
const GEOLOCATION_ERROR_CODE = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3
} as const;

/**
 * Tuned for centering a map on a city or neighbourhood, not for turn-by-turn:
 * high accuracy costs seconds and battery for precision the camera never uses,
 * and a five-minute-old fix is still a perfectly good map center.
 */
export const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 300_000
};

/**
 * Geolocation is exposed to secure contexts only, so a dev server reached over
 * a LAN IP (rather than localhost) has no API at all — not a denied permission.
 */
export const isGeolocationSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  "geolocation" in navigator &&
  window.isSecureContext;

const statusFromError = (error: GeolocationPositionError) => {
  switch (error.code) {
    case GEOLOCATION_ERROR_CODE.PERMISSION_DENIED:
      return EGeolocationStatus.DENIED;
    case GEOLOCATION_ERROR_CODE.TIMEOUT:
      return EGeolocationStatus.TIMEOUT;
    default:
      return EGeolocationStatus.UNAVAILABLE;
  }
};

/**
 * Thin wrapper over `navigator.geolocation` — device concerns only, no map and
 * no store, so it stays testable and reusable.
 *
 * `requestLocation` resolves with the outcome instead of only writing state, so
 * a caller acting on the fix does not have to wait for a re-render to learn
 * which way the request went.
 */
export const useGeolocation = () => {
  const [status, setStatus] = useState<EGeolocationStatus>(() =>
    isGeolocationSupported()
      ? EGeolocationStatus.IDLE
      : EGeolocationStatus.UNSUPPORTED
  );
  const [fix, setFix] = useState<GeolocationFix | null>(null);
  const isMountedRef = useRef(true);

  // Re-armed on every mount, not just cleared on unmount: StrictMode mounts,
  // unmounts and remounts effects, so a guard that is only ever cleared would
  // leave the hook permanently unable to publish its own result.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Read the stored permission before ever prompting, so the UI can render a
  // blocked state instead of firing a dialog the browser will silently refuse.
  useEffect(() => {
    if (!isGeolocationSupported() || !navigator.permissions?.query) return;

    let isSubscribed = true;

    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((permission) => {
        if (!isSubscribed || permission.state !== "denied") return;
        setStatus(EGeolocationStatus.DENIED);
      })
      // Some browsers reject or omit this query entirely; no answer just means
      // "find out when the user asks".
      .catch(() => {});

    return () => {
      isSubscribed = false;
    };
  }, []);

  const requestLocation = useCallback((): Promise<GeolocationResult> => {
    if (!isGeolocationSupported()) {
      setStatus(EGeolocationStatus.UNSUPPORTED);
      return Promise.resolve({
        status: EGeolocationStatus.UNSUPPORTED,
        fix: null
      });
    }

    setStatus(EGeolocationStatus.LOCATING);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const nextFix: GeolocationFix = {
            longitude: position.coords.longitude,
            latitude: position.coords.latitude,
            accuracy: position.coords.accuracy
          };

          if (isMountedRef.current) {
            setFix(nextFix);
            setStatus(EGeolocationStatus.SUCCESS);
          }

          resolve({ status: EGeolocationStatus.SUCCESS, fix: nextFix });
        },
        (error) => {
          const nextStatus = statusFromError(error);

          if (isMountedRef.current) setStatus(nextStatus);

          resolve({ status: nextStatus, fix: null });
        },
        GEOLOCATION_OPTIONS
      );
    });
  }, []);

  return {
    status,
    fix,
    requestLocation,
    isSupported: status !== EGeolocationStatus.UNSUPPORTED,
    isLocating: status === EGeolocationStatus.LOCATING,
    /**
     * Permission is denied. The app cannot re-prompt — only the browser's own
     * site settings can lift this.
     */
    isBlocked: status === EGeolocationStatus.DENIED
  };
};

export default useGeolocation;
